import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import Overlay from '../Overlay';
import SuiteForm from '../SuiteForm';
import PropertyWalkThroughForm from '../PropertyWalkThroughForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectPropertyWalkThroughFormIsVisible } from 'src/store/selectors/propertyWalkThroughForm';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import { useSaveSuite } from 'src/SharedCustomHooks/UseSaveSuite/useSaveSuite';
import SuitesActionSelect from 'src/components/SuiteActionSelect/SuiteActionSelect';
import {
  SUITE_ACTION_PARAM,
  SUITE_DEEP_LINK_ACTION,
} from 'src/components/SuitesTable/suiteDeepLinkActions';
import {
  useBulkWalkthroughs,
  UseBulkWalkthroughsReturn,
} from 'src/components/SuitesTable/CustomHooks/useBulkWalkthroughs';
import {
  useBulkKeyPickups,
  UseBulkKeyPickupsReturn,
} from 'src/components/SuitesTable/CustomHooks/useKeyPickups';
import BulkAddWalkthroughsDialogue from 'src/components/BulkOperations/Walkthroughs/BulkAddWalkthroughsDialogue/BulkAddWalkthroughsDialogue';
import BulkAddKeyPickupsDialogue from 'src/components/BulkOperations/KeyPickups/BulkAddKeyPickupsDialogue/BulkAddKeyPickupsDialogue';
import SuitesEmptyState from 'src/components/SuitesTable/SuitesEmptyState';
import SuitesTableView from 'src/components/SuitesTable/SuitesTableView';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import GenericSearchBar from '../GenricSearchBar/GenericSearchBar';
import { variables } from 'src/Tenants';
import { ISuite } from 'src/components/SharedTypes';
import BulkActivateWalkthroughsDialogue from 'src/components/BulkOperations/Walkthroughs/BulkActivationWalkthroughsDialogue/BulkActivateWalkthroughsDialogue';

const SuitesTable = (props: any) => {
  const [state, setState] = useState({
    showOverlay: false,
    activatedOptions: ['Guided Walk Through', 'Self-Guided Walk Through'],
    suiteData: {},
  });

  const [openBulkAddWalkthroughDialogue, setOpenBulkAddWalkthroughDialogue] =
    useState<boolean>(false);

  const [
    openBulkActivateWalkthroughDialogue,
    setOpenBulkActivateWalkthroughDialogue,
  ] = useState<boolean>(false);

  const [openBulkKeyPickupDialogue, setOpenBulkKeyPickupDialogue] =
    useState<boolean>(false);

  const {
    bulkWalkthroughsCreated,
    isLoading: walkthroughsLoading,
    isBulkCreationSuccess,
    error: walkthroughsError,
    saveBulkWalkthroughs,
    clearBulkWalkthroughsState,
  }: UseBulkWalkthroughsReturn = useBulkWalkthroughs();

  const {
    bulkKeyPickupsCreated,
    isLoading: keyPickupsLoading,
    error: keyPickupsError,
    saveBulkKeyPickups,
    clearBulkKeyPickupsState,
  }: UseBulkKeyPickupsReturn = useBulkKeyPickups();

  // Legacy Redux state - Todo: refactor this component to localize all this state.
  const search: any = useSelector(selectSearch);
  const user: any = useSelector(selectAuthenticatedUser);
  const propertyWalkThroughFormIsVisible: any = useSelector(
    selectPropertyWalkThroughFormIsVisible
  );

  const dispatch: Dispatch<any> = useDispatch();
  const history: any = useHistory();

  const { saveSuite } = useSaveSuite();
  const { showOverlay } = state;

  const handleSaveSuite = async (
    suite: any,
    suiteAreas: any,
    floorImage: any
  ): Promise<{ ok: boolean }> => {
    const { ok } = await saveSuite(
      suite,
      suiteAreas,
      floorImage,
      props.property?.propertyId
    );

    if (!ok) {
      return { ok };
    }

    if (!suite.id) {
      dispatch(
        fetchPropertyCounts(
          props.property?.propertyId,
          user.id,
          user.roles[0].role.name
        )
      );
    }

    await props.fetchPropertySuites();

    return { ok };
  };

  const [searchTermFilterData, setSearchTermFilterData] = useState<string>('');

  interface IFilteredSuite extends ISuite {
    sortableHomeownerName: string;
  }
  const filteredSuiteData: IFilteredSuite[] = useMemo(() => {
    let filtered: IFilteredSuite[] = props.suites;

    if (user.roles[0].role.name === 'Home Owner') {
      filtered = filtered.filter(
        (suite: IFilteredSuite) => suite.homeownerId === user.id
      );
    }

    if (searchTermFilterData.trim()) {
      const searchTermLowercase: string = searchTermFilterData.toLowerCase();

      filtered = filtered.filter((suite: ISuite) => {
        const numberMatch = suite.number
          .toLowerCase()
          .includes(searchTermLowercase);

        const nameMatch =
          suite.building?.toLowerCase().includes(searchTermLowercase) || false;

        return numberMatch || nameMatch;
      });
    }

    return filtered.map((suite: ISuite) => {
      return {
        ...suite,
        sortableHomeownerName:
          (suite.homeOwners?.firstname ?? '') +
          ' ' +
          (suite.homeOwners?.lastname ?? ''),
      };
    });
  }, [searchTermFilterData, props.suites]);

  const handleAddSuite = () => {
    setState({
      ...state,
      suiteData: { propertyId: props.property?.id },
      showOverlay: true,
    });
  };

  const handleOpenBulkWalkthroughsDialogue = () => {
    setOpenBulkAddWalkthroughDialogue(true);
  };

  const handleOpenBulkKeyPickupDialogue = () => {
    setOpenBulkKeyPickupDialogue(true);
  };

  const handleOpenBulkActivateWalkthroughsDialogue = () => {
    setOpenBulkActivateWalkthroughDialogue(true);
  };

  const handleCloseSuiteForm = () => {
    setState({ ...state, showOverlay: false });
  };

  const handleCloseBulkWalkthroughs = () => {
    setOpenBulkAddWalkthroughDialogue(false);
  };

  const handleCloseBulkKeyPickupDialogue = () => {
    setOpenBulkKeyPickupDialogue(false);
  };

  const handleCloseBulkActivateWalkthroughsDialogue = () => {
    setOpenBulkActivateWalkthroughDialogue(false);
  };

  /* On mount, open the bulk dialogue named by the ?action= deep link (set by the suite page's
   * "manage from property" callout), then drop the param so a refresh won't reopen it. */
  useEffect(() => {
    const action: string | null = new URLSearchParams(
      window.location.search
    ).get(SUITE_ACTION_PARAM);

    if (!action) {
      return;
    }

    if (action === SUITE_DEEP_LINK_ACTION.addWalkthroughs) {
      handleOpenBulkWalkthroughsDialogue();
    }

    if (action === SUITE_DEEP_LINK_ACTION.activateWalkthroughs) {
      handleOpenBulkActivateWalkthroughsDialogue();
    }

    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const navigateToSuite = (id: any) => {
    history.push({
      pathname: PATHS.SUITES_VIEW.replace(':id', props.property?.id + '&' + id),
      state: { property: props.property },
    });

    return;
  };

  return (
    <>
      <GenericSearchBar
        onSearch={(e: any) => setSearchTermFilterData(e.target.value)}
        placeholder={'Search ' + variables.suiteName + '...'}
      >
        <SuitesActionSelect
          user={user}
          property={props.property}
          onAddSuite={handleAddSuite}
          handleOpenBulkWalkthroughsDialogue={
            handleOpenBulkWalkthroughsDialogue
          }
          handleOpenBulkKeyPickupDialogue={handleOpenBulkKeyPickupDialogue}
          handleOpenBulkActivateWalkthroughsDialogue={
            handleOpenBulkActivateWalkthroughsDialogue
          }
          fetchPropertySuites={props.fetchPropertySuites}
          filteredSuiteData={filteredSuiteData}
        />
      </GenericSearchBar>
      {props.loading || filteredSuiteData.length > 0 ? (
        <SuitesTableView
          filteredSuites={filteredSuiteData}
          onSuiteClick={navigateToSuite}
          onToggleMobileSync={props.onToggleMobileSync}
          fetchPropertySuites={props.fetchPropertySuites}
          loading={props.loading}
          persistKey={`${PAGINATION_KEYS.suites}-${props.property?.id}`}
        />
      ) : (
        <SuitesEmptyState
          search={search}
          user={user}
          property={props.property}
          onAddSuite={handleAddSuite}
        />
      )}
      {showOverlay && (
        <SuiteForm
          key={(state.suiteData as any)?.id ?? 'add'}
          initialSuite={state.suiteData}
          confirmOnSave={!(state.suiteData as any)?.id}
          onClose={handleCloseSuiteForm}
          onSaveSuite={handleSaveSuite}
        />
      )}
      <Overlay
        showOverlay={propertyWalkThroughFormIsVisible}
        content={<PropertyWalkThroughForm />}
      />
      <BulkAddWalkthroughsDialogue
        isOpen={openBulkAddWalkthroughDialogue}
        onClose={handleCloseBulkWalkthroughs}
        userId={user.id}
        selectedSuiteIds={[]}
        onSave={saveBulkWalkthroughs}
        propertyId={props.property?.id}
        isLoading={walkthroughsLoading}
        bulkWalkthroughsCreated={bulkWalkthroughsCreated}
        isBulkCreationSuccess={isBulkCreationSuccess}
        onSetBulkSuccess={clearBulkWalkthroughsState}
      />
      <BulkActivateWalkthroughsDialogue
        isOpen={openBulkActivateWalkthroughDialogue}
        onClose={handleCloseBulkActivateWalkthroughsDialogue}
        userId={user.id}
        propertyId={props.property?.id}
      />
      <BulkAddKeyPickupsDialogue
        isOpen={openBulkKeyPickupDialogue}
        onClose={handleCloseBulkKeyPickupDialogue}
        userId={user.id}
        selectedSuiteIds={[]}
        onSave={saveBulkKeyPickups}
        propertyId={props.property?.id}
        isLoading={keyPickupsLoading}
        onSetBulkSuccess={clearBulkKeyPickupsState}
        bulkKeyPickupsCreated={bulkKeyPickupsCreated}
      />
    </>
  );
};

export default SuitesTable;
